import Controller from '@ember/controller';
import { action } from '@ember/object';
import type { ConnectorType } from 'scaleflow/utils/connectors-config';
import type StoreService from 'scaleflow/services/store';
import type IntlService from 'ember-intl/services/intl';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import { HOST } from 'scaleflow/adapters/application';
import {
  isGitlabSealConfigured,
  sealGitlabCredential,
} from 'scaleflow/utils/gitlab-seal';

/**
 * A rejection from `adapter.ajax` that came back as a JSON:API error document.
 *
 * `code` on the outer object is ember-data's *error class* marker
 * (`'InvalidError'` for a 422) — not the server's error code, which lives on
 * `errors[0].code`. See `@warp-drive/legacy/adapter/error`.
 */
type AdapterErrorLike = {
  code?: string;
  errors?: { status?: string; code?: string; title?: string }[];
};

/**
 * `ok: false` blocks the save and names the reason. `ok: true` proceeds —
 * `unverified` only records whether GitLab actually vouched for the token, so a
 * caller could weaken its copy; it is not a failure.
 */
export type PreflightResult =
  { ok: false; code: string } | { ok: true; unverified: boolean };

/** The scopes clone-consumer needs to list projects and clone them. */
const REQUIRED_SCOPES = ['read_api', 'read_repository'];

/** GitLab's `error`/`error_description` discriminators on a denial body. */
async function readErrorDetail(
  response: Response,
): Promise<{ error: string | null; description: string | null }> {
  try {
    const body = (await response.json()) as {
      error?: unknown;
      error_description?: unknown;
    };
    return {
      error: typeof body.error === 'string' ? body.error : null,
      description:
        typeof body.error_description === 'string'
          ? body.error_description
          : null,
    };
  } catch {
    return { error: null, description: null };
  }
}

/**
 * Asks GitLab directly whether a token is usable, before sealing it.
 *
 * **This is an optimization, not a gate.** A *response* is trustworthy; a
 * *thrown* `fetch` is not. A CORS rejection, a DNS failure, a mixed-content
 * block and a down instance all surface identically as
 * `TypeError: Failed to fetch`, so a throw carries no information at all —
 * blocking on it would lock out exactly the self-hosted cohort whose instance
 * sends no CORS headers but whose token is fine. The same checks run
 * authoritatively in clone-consumer; this one only buys a fast inline error.
 */
export async function preflightGitlabToken(
  instanceUrl: string,
  token: string,
): Promise<PreflightResult> {
  // Rejected outright rather than left to the browser's mixed-content block:
  // the real reason is that clone-consumer would otherwise send the PAT over
  // plaintext at clone time.
  if (!instanceUrl.startsWith('https://')) {
    return { ok: false, code: 'invalid_instance_url' };
  }

  let response: Response;
  try {
    response = await fetch(
      `${instanceUrl}/api/v4/personal_access_tokens/self`,
      { headers: { 'PRIVATE-TOKEN': token } },
    );
  } catch {
    return { ok: true, unverified: true };
  }

  if (response.status === 401) {
    return { ok: false, code: 'invalid_token' };
  }

  if (response.status === 403) {
    const { error, description } = await readErrorDetail(response);

    if (error === 'insufficient_granular_scope') {
      // GitLab gates token introspection behind a *user*-level fine-grained
      // permission of its own (`Personal Access Token: Read`), separate from
      // the project/group permissions `token-help` tells people to grant.
      //
      // Unlike the rest of this 403 branch, this one specific permission is
      // worth naming directly: `storeCredential` in clone-consumer calls this
      // same endpoint to identify the token at connect time, so a token
      // missing it will fail there regardless — better to say so now than let
      // it fail slowly, after sealing, with a confusing error.
      if (description?.includes('Personal Access Token: Read')) {
        return { ok: false, code: 'missing_pat_read_permission' };
      }
      // Any other granular-permission denial here (e.g. missing Project:
      // Read) says nothing about whether the token can actually list or
      // clone — that depends on permissions this endpoint cannot see. Fall
      // through and let the async job be the authority, same as a throw.
      return { ok: true, unverified: true };
    }
    return { ok: false, code: 'invalid_token' };
  }

  // Any other non-2xx says something about the instance, not the credential.
  if (!response.ok) return { ok: true, unverified: true };

  let info: {
    scopes?: unknown;
    revoked?: boolean;
    active?: boolean;
    granular?: boolean;
  };
  try {
    info = (await response.json()) as typeof info;
  } catch {
    return { ok: true, unverified: true };
  }

  // A 200 whose body is not the shape we expect is as uninformative as a throw
  // — proceed rather than invent a verdict from it.
  if (!Array.isArray(info.scopes)) return { ok: true, unverified: true };

  // A fine-grained token's `scopes` is always the literal `["granular"]`,
  // never the real permission list — verified live against gitlab.com.
  // `REQUIRED_SCOPES.filter` against that would call every correctly
  // configured granular token under-scoped, which is the exact false block
  // this preflight exists to avoid. There is no way to read individual
  // granular permissions from this endpoint, so skip the check rather than
  // invent a verdict; clone-consumer's list job is the real authority here.
  if (!info.granular) {
    const missing = REQUIRED_SCOPES.filter((s) => !info.scopes!.includes(s));
    if (missing.length) return { ok: false, code: 'insufficient_scope' };
  }

  // `active`/`revoked` rather than comparing `expires_at`: that field is a bare
  // date, so a local comparison rejects a token on its final valid day.
  if (info.revoked || info.active === false) {
    return { ok: false, code: 'token_expired' };
  }

  // A granular token's scopes couldn't actually be checked above, so it is
  // not "verified" in the same sense a classic token's clean scope check is.
  return { ok: true, unverified: !!info.granular };
}

export interface GitlabPathProject {
  id: number;
  path: string;
}

export interface GitlabPathResult {
  path: string;
  kind: 'group' | 'project';
  projects?: GitlabPathProject[];
  error?: string;
}

/**
 * Resolves a group or project path against the instance directly from the
 * browser — never through Scaleflow's API. Neither `/groups` nor
 * `/projects?membership=true` can be trusted to self-enumerate a group-scoped
 * granular token (both are user-level operations and 403 for one), so the
 * user names each path and this confirms it rather than discovering it.
 *
 * Tries the group listing first; a `404` there (not a group, or a project
 * path was given) falls back to resolving it as a single project.
 */
async function resolveGitlabPath(
  instanceUrl: string,
  token: string,
  path: string,
): Promise<GitlabPathResult> {
  const headers = { 'PRIVATE-TOKEN': token };
  const encoded = encodeURIComponent(path);

  try {
    const groupRes = await fetch(
      `${instanceUrl}/api/v4/groups/${encoded}/projects?per_page=100`,
      { headers },
    );
    if (groupRes.ok) {
      const body = (await groupRes.json()) as {
        id: number;
        path_with_namespace: string;
      }[];
      return {
        path,
        kind: 'group',
        projects: body.map((p) => ({ id: p.id, path: p.path_with_namespace })),
      };
    }
  } catch {
    // Falls through to the project lookup below.
  }

  try {
    const projectRes = await fetch(
      `${instanceUrl}/api/v4/projects/${encoded}`,
      {
        headers,
      },
    );
    if (projectRes.ok) {
      const p = (await projectRes.json()) as {
        id: number;
        path_with_namespace: string;
      };
      return {
        path,
        kind: 'project',
        projects: [{ id: p.id, path: p.path_with_namespace }],
      };
    }
    return { path, kind: 'project', error: `HTTP ${projectRes.status}` };
  } catch {
    return { path, kind: 'project', error: 'network error' };
  }
}

/** What one `listGitlabPaths` pass produces. */
export interface GitlabPathListing {
  results: GitlabPathResult[];
  /**
   * Subgroups found under a listed group that themselves have direct
   * projects, not already named in the input. Not a rendering concern — the
   * component appends these to the paths field and re-lists, so they show up
   * through the same preview everything else does, one level deeper each
   * pass.
   */
  discoveredPaths: string[];
}

/**
 * `/groups/{path}/projects` never mentions subgroups — GitLab has no
 * endpoint that lists "everything reachable under this group", so a group
 * with subgroups looks identical to one without unless subgroups are
 * checked explicitly.
 *
 * Only subgroups that themselves have at least one direct project are
 * returned. A subgroup with none is not worth surfacing here — it may itself
 * have sub-subgroups with projects, but that is exactly what the *next*
 * `listGitlabPaths` pass over it discovers, once it is in the path list.
 */
async function listGroupSubgroupsWithProjects(
  instanceUrl: string,
  token: string,
  groupPath: string,
): Promise<string[]> {
  const headers = { 'PRIVATE-TOKEN': token };

  let subgroups: { full_path: string }[];
  try {
    const res = await fetch(
      `${instanceUrl}/api/v4/groups/${encodeURIComponent(groupPath)}/subgroups?per_page=100`,
      { headers },
    );
    if (!res.ok) return [];
    subgroups = (await res.json()) as { full_path: string }[];
  } catch {
    return [];
  }

  const withProjects: string[] = [];
  for (const subgroup of subgroups) {
    try {
      const res = await fetch(
        `${instanceUrl}/api/v4/groups/${encodeURIComponent(subgroup.full_path)}/projects?per_page=1`,
        { headers },
      );
      if (res.ok) {
        const projects = (await res.json()) as unknown[];
        if (projects.length > 0) withProjects.push(subgroup.full_path);
      }
    } catch {
      // A subgroup we cannot check is not one we can silently add.
    }
  }
  return withProjects;
}

export default class ConnectorsGitlabController extends Controller {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare currentUser: { tenantId?: string };
  declare model: { connector: ConnectorType; integrations: any[] };

  @tracked loading: boolean = false;

  /** Translated message for the last failed credential connect, if any. */
  @tracked connectError: string | null = null;

  /** True once polling gave up while `credential_status` was still `PENDING`. */
  @tracked pollTimedOut = false;

  /** True while `disconnectGitlab`'s `destroyRecord` is in flight. */
  @tracked deleting = false;

  /** Translated message for the last failed disconnect, if any. */
  @tracked deleteError: string | null = null;

  /**
   * Poll schedule, as class fields so tests can collapse them to zero — the
   * same treatment `operations/teams/team/settings`'s autosave gives its
   * debounce. Giving up at ~90s is deliberate: `credential_status` is set by
   * `storeCredential`, an async job off a queue — a transient failure there
   * can retry more than once, and the record carries the answer whenever it
   * lands regardless of whether anyone is watching.
   */
  pollBackoffMs = [1000, 2000, 3000, 5000, 5000, 10000];
  pollFallbackMs = 15000;
  pollDeadlineMs = 90000;

  get isPolling(): boolean {
    return this.pollCredentialStatus.isRunning;
  }

  /**
   * A `FAILED` credential means nothing was actually stored — `provider_id`
   * never left `'pending'`, so there is nothing to lock the form against.
   * Every other state (still `PENDING`, or `STORED`) is a real credential and
   * gets the disabled, pre-filled read-only view instead.
   */
  get gitlabFormDisabled(): boolean {
    const integration = this.existingIntegration;
    return !!integration && integration.credential_status !== 'FAILED';
  }

  get isPending() {
    return [...this.model.integrations].some(
      (i) =>
        i.provider_name === this.model.connector.id &&
        i.provider_id === 'pending',
    );
  }

  get isConnected() {
    return [...this.model.integrations].some(
      (i) =>
        i.provider_name === this.model.connector.id &&
        i.provider_id !== 'pending',
    );
  }

  /**
   * The row for this page's connector — connected, still pending, or failed.
   * GitLab's connect form reads from this to render itself disabled and
   * pre-filled once a row exists (see `gitlabFormDisabled`) — there is
   * deliberately no edit-in-place flow, so this is what "you're connected"
   * looks like instead of a plain badge.
   */
  get existingIntegration(): any {
    return [...this.model.integrations].find(
      (i) => i.provider_name === this.model.connector.id,
    );
  }

  /**
   * Single source of truth for both the header badge and the CTA banner —
   * having the template re-derive this from `existingIntegration` in two
   * places is what let the two drift out of sync with each other.
   */
  get badgeState(): 'failed' | 'connected' | 'pending' | null {
    if (this.existingIntegration?.credential_status === 'FAILED')
      return 'failed';
    if (this.isConnected) return 'connected';
    if (this.isPending) return 'pending';
    return null;
  }

  /**
   * Resolves every comma-separated path the user typed, one at a time. Runs
   * sequentially rather than `Promise.all` so a slow or unreachable instance
   * degrades to a slow preview rather than one failure aborting the rest.
   *
   * Each `group` result also gets its subgroups checked for projects.
   * `discoveredPaths` is the caller's problem, not this method's — it only
   * reports what exists; the component decides whether to fold them into the
   * input and ask again.
   */
  @action
  async listGitlabPaths(
    instanceUrl: string,
    token: string,
    paths: string[],
  ): Promise<GitlabPathListing> {
    const results: GitlabPathResult[] = [];
    const discovered = new Set<string>();

    for (const path of paths) {
      const result = await resolveGitlabPath(instanceUrl, token, path);
      results.push(result);

      if (result.kind === 'group' && !result.error) {
        const subgroupPaths = await listGroupSubgroupsWithProjects(
          instanceUrl,
          token,
          path,
        );
        for (const subgroupPath of subgroupPaths) {
          if (!paths.includes(subgroupPath)) discovered.add(subgroupPath);
        }
      }
    }

    return { results, discoveredPaths: [...discovered] };
  }

  /**
   * Reloads the record until `storeCredential` — the async job the POST
   * enqueues — reports back. The POST itself only ever returns `202`; the
   * record starts `credential_status: 'PENDING'` and `provider_id: 'pending'`,
   * and clone-consumer PATCHes the real outcome onto it later via the
   * integration-settings callback.
   *
   * Stops on `STORED` **or** `FAILED` — a failed job that only stopped on
   * `STORED` would poll until the deadline every time.
   *
   * This is a different job from the repo-list one: `credential_status`
   * covers "is there a usable credential at all", `repos_status` (polled
   * separately, from the wizard) covers "has it listed anything yet". A
   * `FAILED` credential never gets a repo-list job at all.
   *
   * `restartableTask` cancels on route teardown and on a second `perform()`,
   * which covers the double-click case without hand-rolled cleanup.
   */
  pollCredentialStatus = restartableTask(async (id: string) => {
    const started = Date.now();
    let attempt = 0;

    while (Date.now() - started < this.pollDeadlineMs) {
      await timeout(this.pollBackoffMs[attempt] ?? this.pollFallbackMs);
      attempt++;

      const setting: any = await this.store.findRecord(
        'integration-setting',
        id,
        { reload: true },
      );

      if (setting?.credential_status !== 'PENDING') {
        return setting;
      }
    }

    this.pollTimedOut = true;
    return null;
  });

  /**
   * Connects a provider that authenticates with credentials the user pastes in
   * (GitLab) rather than an OAuth app install (GitHub).
   *
   * preflight → seal → POST the blob → poll. The plaintext token never leaves
   * the browser: what goes over the wire is `{ key_id, ciphertext }`, which
   * api-prod stores without being able to read. The token is only ever an
   * argument and a local here — never assigned to a property, logged, or
   * echoed back into the error message.
   */
  @action
  async connectWithCredentials(
    instanceUrl: string,
    token: string,
    paths: string[],
  ) {
    this.loading = true;
    this.connectError = null;
    this.pollTimedOut = false;

    try {
      const result = await preflightGitlabToken(instanceUrl, token);
      if (!result.ok) {
        this.connectError = this.mapConnectErrorCode(result.code);
        return;
      }

      // A build with no real public key can only produce blobs nobody holds the
      // private half of, so this is checked before the token is touched rather
      // than surfaced as a failed request afterwards.
      if (!isGitlabSealConfigured() || !this.currentUser.tenantId) {
        this.connectError = this.intl.t(
          'connectors.gitlab.errors.not_configured',
        );
        return;
      }

      const sealed = await sealGitlabCredential(
        this.currentUser.tenantId,
        instanceUrl,
        token,
      );

      const adapter = this.store.adapterFor('application') as any;
      const json = await adapter.ajax(`${HOST}/integration-settings`, 'POST', {
        data: {
          data: {
            type: 'integration-settings',
            attributes: {
              provider_name: this.model.connector.id,
              // Plaintext, display only. The authoritative copy is the one
              // sealed inside the blob — that is what clone-consumer derives
              // the git host from. They are not interchangeable.
              instance_url: instanceUrl,
              // Not secret — the groups/projects the user named to scan.
              // xray-workflow re-lists from these paths itself (it cannot
              // self-enumerate a group-scoped token any more than the
              // browser can — see the blueprint) rather than trusting
              // whatever the browser preview showed.
              group_paths: paths,
              sealed,
            },
          },
        },
      });

      const id: string | undefined = json?.data?.id;
      if (id) await this.pollCredentialStatus.perform(id);

      // The 202 body carries only an id, so the badge still needs the list
      // refreshed to see the job's verdict land on `provider_id`/`credential_status`.
      await this.store.findAll('integration-setting', { reload: true });
    } catch (error) {
      this.connectError = this.mapConnectError(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Danger Zone: removes the credential entirely. `destroyRecord` DELETEs
   * `/integration-settings/:id`; the API's own delete handler is what tells
   * clone-consumer to drop the sealed secret, so there is nothing else this
   * needs to clean up client-side.
   */
  @action
  async disconnectGitlab() {
    const integration = this.existingIntegration;
    if (!integration) return;

    this.deleting = true;
    this.deleteError = null;

    try {
      await integration.destroyRecord();
    } catch {
      this.deleteError = this.intl.t('connectors.gitlab.errors.network');
    } finally {
      this.deleting = false;
    }
  }

  /**
   * Turns a failed connect into a translated message.
   *
   * Mapping is keyed on the server's `code`, never on `title` — `title` is
   * display copy the API owner can reword without warning. An unrecognized
   * code falls back to `generic` rather than rendering a raw translation key.
   */
  mapConnectError(error: unknown): string {
    const adapterError = error as AdapterErrorLike | null | undefined;
    const isValidationError =
      adapterError?.code === 'InvalidError' ||
      adapterError?.errors?.[0]?.status === '422';

    // Anything that isn't the API's validation response — a network failure, a
    // 5xx, a timeout — is a different problem than a bad URL or token, and gets
    // its own message rather than advice about credentials.
    if (!isValidationError) {
      return this.intl.t('connectors.gitlab.errors.network');
    }

    return this.mapConnectErrorCode(adapterError?.errors?.[0]?.code);
  }

  /**
   * The shared tail of the mapping. The error-code vocabulary is the same for
   * the local preflight and for server-returned errors, so one translation
   * table serves both.
   */
  mapConnectErrorCode(code?: string): string {
    const key = code ? `connectors.gitlab.errors.${code}` : null;
    return key && this.intl.exists(key)
      ? this.intl.t(key)
      : this.intl.t('connectors.gitlab.errors.generic');
  }
}
